import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useForm } from "react-hook-form";
import queryString from "query-string";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

import { SaveLoader } from "@/_components";
import { userLoginSuccess } from "../_redux/actions/accountActions";
import { accountService } from "@/_services";

import makeStyles from '@mui/styles/makeStyles';
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  submit: { margin: theme.spacing(3, 0, 2) },
  buttonIco: { width: 24, height: 24, marginRight: 10, marginTop: 7 },
  inputIco: { opacity: 0.6, color: "#1c355e" },
}));

function ResetPassword({ history }) {
  const TokenStatus = {
    Validating: "Validating",
    Valid: "Valid",
    Invalid: "Invalid",
  };

  const classes = useStyles();
  const { t } = useTranslation();
  const [token, setToken] = useState(null);
  const [userID, setUserID] = useState(null);
  const [tokenStatus, setTokenStatus] = useState(TokenStatus.Validating);
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState({ showPassword: false });
  const { register, errors, handleSubmit, watch } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    let { token, userId } = queryString.parse(location.search);
    // remove token from url to prevent http referer leakage
    history.replace(location.pathname);
    //Regexs to make the token right
    token = token.replace(/\+/g, "%2b");
    token = token.replace(/\//g, "%2f");
    token = token.replace(/=/g, "%3d");

    let userID = parseInt(userId);

    accountService
      .validateResetToken(userID, token)
      .then(() => {
        setToken(token);
        setUserID(userID);
        setTokenStatus(TokenStatus.Valid);
      })
      .catch(() => {
        setTokenStatus(TokenStatus.Invalid);
      });
  }, []);

  function getForm() {
    const onSubmit = (data) => {
      setSubmitting(true);
      const password = data.password;

      accountService
        .resetPassword(userID, token, password)
        .then((rtn) => {
          // toast.success(t("account.passwordResetSuccess"));
          handleLogin(rtn.email, password);
        })
        .catch((error) => {
          setSubmitting(false);
          toast.error(error);
        })
        .finally(() => setSubmitting(false));
    };
    const handleClickShowPassword = () => {
      setValues({ ...values, showPassword: !values.showPassword });
    };
    const handleMouseDownPassword = (event) => {
      event.preventDefault();
    };
    const handleLogin = (email, password) => {
      accountService
        .login(email, password)
        .then(() => {
          userLoginSuccess();
          setSubmitting(false);
          const { from } = { from: { pathname: "/" } };
          history.push(from);
        })
        .catch((error) => {
          setSubmitting(false);
          toast.error(error);
        });
    };

    return (
      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          id="password"
          label={t("account.password")}
          name="password"
          autoComplete="current-password"
          type={values.showPassword ? "text" : "password"}
          error={!!errors.password}
          helperText={errors.password ? errors.password.message : ""}
          inputRef={register({
            required: { value: true, message: t("account.passwordRequired") },
            pattern: {
              value:
                /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,24}$/,
              message: t("account.passwordPattern"),
            },
          })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon className={classes.inputIco} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  size="large">
                  {values.showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          id="confirmPassword"
          label={t("account.confirmPassword")}
          name="confirmPassword"
          autoComplete="current-password"
          type={"password"}
          error={!!errors.confirmPassword}
          helperText={
            errors.confirmPassword ? errors.confirmPassword.message : ""
          }
          inputRef={register({
            required: {
              value: true,
              message: t("account.confirmPasswordRequired"),
            },
            validate: (value) => {
              if (value === watch("password")) {
                return true;
              } else {
                return t("account.confirmPasswordValid");
              }
            },
          })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon className={classes.inputIco} />
              </InputAdornment>
            ),
          }}
        />
        <Grid container alignItems="center" justifyContent="center" direction="column">
          <Grid item>
            {submitting === false ? (
              <Button
                type="submit"
                size="medium"
                disabled={submitting}
                variant="contained"
                color="primary"
                className={classes.submit}
                style={{ width: 240, height: 50 }}
              >
                {t("account.resetPasswordButton")}
              </Button>
            ) : (
              <SaveLoader />
            )}
          </Grid>
          <Grid item>
            <img src={"./src/_images/orLine.png"} alt="OR" />
          </Grid>
          <Grid item style={{ paddingBottom: 15 }}>
            <RouterLink to="/login" style={{ textDecoration: "none" }}>
              <Button
                type="submit"
                size="medium"
                disabled={submitting}
                variant="contained"
                color="secondary"
                style={{ width: 240, height: 50 }}
              >
                {t("account.backToSignInButton")}
              </Button>
            </RouterLink>
          </Grid>
        </Grid>
      </form>
    );
  }

  function getBody() {
    switch (tokenStatus) {
      case TokenStatus.Valid:
        return getForm();
      case TokenStatus.Invalid:
        return (
          <Typography variant="h5">
            {t("account.tokenFailedPart1")}
            <RouterLink to="forgot-password">
              {t("account.tokenFailedPart2")}
            </RouterLink>
            {t("account.tokenFailedPart3")}
          </Typography>
        );
      case TokenStatus.Validating:
        return <div>{t("account.tokenValidation")}</div>;
    }
  }

  return (
    <div>
      <div className="card-body">{getBody()}</div>
    </div>
  );
}

ResetPassword.propTypes = {
  history: PropTypes.object,
  userLoginSuccess: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    userLoginSuccess: state.userLoginSuccess,
  };
}

const mapDispatchToProps = {
  userLoginSuccess,
};

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);
